import { useState } from 'react'
import { DayPicker } from 'react-day-picker'
import { Button, TimeInput } from '@heroui/react'
import { ClockIcon } from '@heroicons/react/24/outline'
import { Time } from '@internationalized/date'
import PropTypes from 'prop-types'

const ScheduleAction = ({ 
  description, 
  executeAt, 
  handleSave, 
  handleCancel, 
  maximumDateTime, 
  minimumDateTime,
  mode, 
  isReadOnly,
  isSubmitting,
  title, 
}) => {
  const [selectedDay, setSelectedDay] = useState(() => executeAt ? new Date(executeAt) : null)
  const [selectedTime, setSelectedTime] = useState(() => executeAt ? new Time(executeAt.getHours(), executeAt.getMinutes()) : null)

  function getSelectedDateTime() {
    if (!selectedDay || !selectedTime) {
      return null
    }

    return new Date(
      selectedDay.getFullYear(),
      selectedDay.getMonth(),
      selectedDay.getDate(),
      selectedTime.hour,
      selectedTime.minute,
      0,
      0
    )
  }

  function isDirty() {
    if (!executeAt || !selectedDay || !selectedTime) {
      return false
    }

    return (
      selectedDay.getFullYear() !== executeAt.getFullYear() ||
      selectedDay.getMonth() !== executeAt.getMonth() ||
      selectedDay.getDate() !== executeAt.getDate() ||
      selectedTime.hour !== executeAt.getHours() ||
      selectedTime.minute !== executeAt.getMinutes()
    )
  }

  function isSaveDisabled() {
    if (isReadOnly || isSubmitting) return true

    if (mode === 'CREATE') return !selectedDay || !selectedTime

    if (mode === 'EDIT') return !isDirty()
    
    return true
  }

  const handleSaveSchedule = () => {
    const selectedDateTime = getSelectedDateTime()

    if (!selectedDateTime || isSaveButtonDisabled) {
      return
    }

    handleSave(selectedDateTime)
  }

  const isSaveButtonDisabled = isSaveDisabled()
  const saveButtonText = mode === 'CREATE' ? 'Create Schedule' : 'Update Schedule'

  return (
    <div>
      <div className="mb-4">
        <p className="text-lg font-semibold">{title}</p>
        <p className="text-content2-foreground">{description}</p>
      </div>

      <DayPicker
        selected={selectedDay}
        onSelect={setSelectedDay}
        mode="single"
        fromMonth={minimumDateTime}
        disabled={isReadOnly || isSubmitting ? () => true : { before: minimumDateTime, after: maximumDateTime }}
      />
  
      <TimeInput
        className="mt-4"
        value={selectedTime}
        onChange={setSelectedTime}
        hourCycle={24}
        endContent={<ClockIcon className="w-7 h-7" />}
        label="Event Time (24 Hours)"
        labelPlacement="outside"
        isDisabled={isReadOnly || isSubmitting}
      />

      <div className="flex justify-end mt-4">
        <Button color="default" onPress={handleCancel} className="mr-2" isDisabled={isSubmitting} fullWidth>
          Cancel
        </Button>
        
        <Button 
          color="secondary"
          onPress={handleSaveSchedule}
          isDisabled={isSaveButtonDisabled}
          fullWidth
          isLoading={isSubmitting}
        >
          {saveButtonText}
        </Button>
      </div>
    </div>
  )
}

ScheduleAction.propTypes = {
  description: PropTypes.string.isRequired,
  executeAt: PropTypes.instanceOf(Date),
  handleCancel: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  maximumDateTime: PropTypes.instanceOf(Date).isRequired,
  minimumDateTime: PropTypes.instanceOf(Date).isRequired,
  mode: PropTypes.oneOf(['CREATE', 'EDIT']).isRequired,
  title: PropTypes.string.isRequired,
}

export default ScheduleAction